import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Bath, BedDouble, ChevronLeft, ChevronRight, Ruler } from "lucide-react";
import type { Property } from "@/types/property";
import { api } from "@/lib/api";
import { SectionHeading } from "@/components/site/SectionHeading";
import { SliderArrows, useScrollTrack } from "@/components/site/ScrollTrack";
import { useLiveProperties } from "@/hooks/useLiveProperties";
import {
  formatArea,
  formatListingTitle,
  formatPrice,
} from "@/lib/format";
import { getPropertyCoverOffsetMap, rankPropertyImages } from "@/lib/property-images";
import { propertyPath } from "@/lib/property-url";
import { SafeImage } from "@/components/site/SafeImage";
import { cn } from "@/lib/utils";

type ListingFilter = "sale" | "rent";

/** Tab order mirrors the reference design (uniqueproperties.ae). */
const FILTER_TABS: { value: ListingFilter; label: string }[] = [
  { value: "sale", label: "For Sale" },
  { value: "rent", label: "For Rent" },
];

/** Cards in the sliding row beneath the showcase. */
const SLIDER_SIZE = 15;

interface TabStock {
  /** Highest-priced listing of this type — the showcase at the top. */
  lead?: Property;
  /** Newest listings of this type, showcase excluded. */
  rest: Property[];
  total: number;
}

const EMPTY_STOCK: TabStock = { rest: [], total: 0 };

/**
 * The showcase must be the priciest listing of the whole type, not just
 * of the newest page, so it comes from its own price-sorted request
 * rather than being picked out of the "newest" page client-side.
 */
async function loadTabStock(type: ListingFilter): Promise<TabStock> {
  const [priciest, newest] = await Promise.all([
    api.listProperties({ listingType: type, sort: "price-desc", pageSize: 1 }),
    api.listProperties({ listingType: type, sort: "newest", pageSize: SLIDER_SIZE + 1 }),
  ]);
  const lead = priciest.items[0];
  const rest = newest.items.filter((p) => p.id !== lead?.id).slice(0, SLIDER_SIZE);
  return { lead, rest, total: newest.total };
}

/**
 * Homepage properties — "New Properties in Dubai", rebuilt to the
 * client's reference (uniqueproperties.ae, Aug 2026): listing-type
 * tabs, a large showcase of the most expensive listing (photo slider +
 * price, Register Now, and Key Highlights), a sliding row of the newest
 * compact cards with bed / bath / sqft specs and a starting price, and a
 * Show More link into the filtered listings page.
 */
export function FeaturedProperties() {
  const { version, connected } = useLiveProperties();
  const [stock, setStock] = useState<Map<ListingFilter, TabStock>>(new Map());
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<ListingFilter>("sale");

  useEffect(() => {
    let cancelled = false;
    Promise.all(
      FILTER_TABS.map((tab) => loadTabStock(tab.value).then((s) => [tab.value, s] as const)),
    )
      .then((entries) => !cancelled && setStock(new Map(entries)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [version]);

  // Never land on an empty tab after the feed arrives — fall through to
  // the first listing type that actually has stock.
  useEffect(() => {
    if (loading || stock.size === 0) return;
    if ((stock.get(filter)?.total ?? 0) > 0) return;
    const first = FILTER_TABS.find((tab) => (stock.get(tab.value)?.total ?? 0) > 0);
    if (first) setFilter(first.value);
  }, [loading, stock, filter]);

  const { lead, rest, total } = stock.get(filter) ?? EMPTY_STOCK;
  const coverOffsets = useMemo(
    () => getPropertyCoverOffsetMap(lead ? [lead, ...rest] : rest),
    [lead, rest],
  );

  return (
    <section className="container-wide py-12 sm:py-16 md:py-24 short:py-10">
      <SectionHeading
        eyebrow={connected ? "Live · New to Market" : "New to Market"}
        size="xl"
        title="New Properties in Dubai."
        body="The newest stock across our sale and rental catalogue — refreshed as listings go live."
        aside={
          <Link to="/properties" className="link-arrow tap-target">
            See All Listings
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M2 7h10m-4-4 4 4-4 4" stroke="currentColor" strokeWidth="1.2" />
            </svg>
          </Link>
        }
      />

      {/* Listing-type tabs — a single scrollable row on narrow screens so
          labels never break mid-word. The scroller
          overlaps the wrapper's rule by 1px so the active underline sits
          on it without the scroller overflowing vertically. */}
      <div className="mt-8 border-b border-[var(--line)] sm:mt-10">
        <div
          role="tablist"
          aria-label="Filter properties by listing type"
          className="-mb-px flex gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:gap-2"
        >
          {FILTER_TABS.map((tab) => {
            const isActive = filter === tab.value;
            return (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setFilter(tab.value)}
                className={cn(
                  "relative min-h-[44px] flex-none whitespace-nowrap px-3 py-3 text-[12px] uppercase tracking-[0.14em] transition-colors sm:px-4 sm:tracking-[0.18em] sm:text-[12.5px]",
                  isActive
                    ? "text-[var(--text)]"
                    : "text-[var(--text-muted)] hover:text-[var(--text-soft)]",
                )}
              >
                {tab.label}
                {isActive && (
                  <span
                    aria-hidden
                    className="absolute inset-x-3 bottom-0 h-px bg-[var(--gold-bright)]"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {loading && <ShowcaseSkeleton />}

      {!loading && lead && (
        <FeaturedShowcase
          key={lead.id}
          property={lead}
          coverOffset={coverOffsets.get(lead.id) ?? 0}
        />
      )}

      {!loading && rest.length > 0 && (
        <CompactCardRow properties={rest} coverOffsets={coverOffsets} />
      )}

      {!loading && total > 0 && (
        <div className="mt-8 flex justify-center sm:mt-10 short:mt-6">
          <Link
            to={`/properties?listingType=${filter}`}
            className="btn btn-ghost btn-block-sm"
          >
            Show More
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M2 7h10m-4-4 4 4-4 4" stroke="currentColor" strokeWidth="1.4" />
            </svg>
          </Link>
        </div>
      )}

      {!loading && total === 0 && (
        <div className="mt-10 border border-dashed border-[var(--line-strong)] p-10 text-center">
          <p
            className="font-display text-[22px] text-[var(--text)] sm:text-[26px]"
            style={{ fontVariationSettings: '"opsz" 36' }}
          >
            No {FILTER_TABS.find((t) => t.value === filter)?.label.toLowerCase()} listings to show right now.
          </p>
          <p className="mt-3 text-[14px] text-[var(--text-soft)]">
            Switch filter or message us — we usually know what's coming to
            market before it's public.
          </p>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Featured showcase — photo slider + price / Register Now / highlights */
/* ------------------------------------------------------------------ */

function FeaturedShowcase({
  property,
  coverOffset,
}: {
  property: Property;
  coverOffset: number;
}) {
  const images = rankPropertyImages(property.images, property.id, { coverOffset });
  const slides = images.slice(0, 5);
  const [slide, setSlide] = useState(0);
  const goTo = (e: React.MouseEvent, next: number) => {
    e.preventDefault();
    e.stopPropagation();
    setSlide(((next % slides.length) + slides.length) % slides.length);
  };

  const title = formatListingTitle(property);
  const highlights = useMemo(() => {
    const seen = new Set<string>();
    const merged: string[] = [];
    for (const entry of [...property.features, ...property.amenities]) {
      const key = entry.trim().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      merged.push(entry.trim());
      if (merged.length === 5) break;
    }
    return merged;
  }, [property]);

  return (
    <div className="mt-8 grid gap-6 sm:mt-10 sm:gap-8 md:grid-cols-12 md:gap-12">
      {/* Photo slider */}
      <Link to={propertyPath(property)} className="group md:col-span-7" aria-label={title}>
        <div className="relative aspect-[4/3] overflow-hidden bg-[var(--surface-2)] sm:aspect-[16/10]">
          <div
            className="flex h-full w-full transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${slide * 100}%)` }}
          >
            {slides.map((src, i) => (
              <div key={src + i} className="h-full w-full flex-none">
                <SafeImage
                  src={src}
                  alt={i === 0 ? title : ""}
                  loading="lazy"
                  sizes="(min-width: 768px) 58vw, 100vw"
                  widths={[640, 960, 1280, 1600]}
                  className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                />
              </div>
            ))}
          </div>

          {slides.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => goTo(e, slide - 1)}
                aria-label="Previous photo"
                className="card-control absolute left-2 top-1/2 z-[1] -translate-y-1/2 bg-[rgba(13,20,34,0.55)] text-white opacity-90 backdrop-blur-sm transition-opacity hover:bg-[rgba(13,20,34,0.8)] hoverable:sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
              >
                <ChevronLeft className="size-5 sm:size-4" aria-hidden />
              </button>
              <button
                type="button"
                onClick={(e) => goTo(e, slide + 1)}
                aria-label="Next photo"
                className="card-control absolute right-2 top-1/2 z-[1] -translate-y-1/2 bg-[rgba(13,20,34,0.55)] text-white opacity-90 backdrop-blur-sm transition-opacity hover:bg-[rgba(13,20,34,0.8)] hoverable:sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
              >
                <ChevronRight className="size-5 sm:size-4" aria-hidden />
              </button>
              <span className="pointer-events-none absolute inset-x-0 bottom-2.5 z-[1] flex items-center justify-center gap-1.5">
                {slides.map((_, i) => (
                  <span
                    key={i}
                    aria-hidden
                    className={cn(
                      "size-1.5 rounded-full transition-colors",
                      i === slide ? "bg-white" : "bg-white/45",
                    )}
                  />
                ))}
              </span>
            </>
          )}

          <span className="absolute left-3 top-3 z-[1] inline-flex items-center bg-white px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[var(--navy)]">
            {badgeLabel(property)}
          </span>
        </div>
      </Link>

      {/* Details panel */}
      <div className="md:col-span-5 flex flex-col">
        <Link to={propertyPath(property)} className="group">
          <h3
            className="font-display text-[24px] leading-[1.1] text-[var(--text)] transition-colors group-hover:text-[var(--gold-bright)] sm:text-[30px] sm:leading-[1.05] md:text-[34px]"
            style={{ fontVariationSettings: '"opsz" 72' }}
          >
            {title}
          </h3>
        </Link>
        <p className="mt-3 text-[13px] uppercase tracking-[0.16em] text-[var(--text-muted)]">
          {property.community}
          {property.subCommunity ? ` · ${property.subCommunity}` : ""}
        </p>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-4 border-y border-[var(--line)] py-4">
          <p
            className="font-display text-[26px] text-[var(--gold-bright)] sm:text-[30px]"
            style={{ fontVariationSettings: '"opsz" 72' }}
          >
            {formatPrice(property.price, property.pricePeriod)}
          </p>
          <Link to={propertyPath(property)} className="btn btn-primary">
            Register Now
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M2 7h10m-4-4 4 4-4 4" stroke="currentColor" strokeWidth="1.4" />
            </svg>
          </Link>
        </div>

        <SpecRow property={property} className="mt-5" />

        {highlights.length > 0 && (
          <div className="mt-6">
            <p className="eyebrow">Key Highlights</p>
            <ul className="mt-4 flex flex-col gap-2.5">
              {highlights.map((highlight) => (
                <li
                  key={highlight}
                  className="flex items-baseline gap-2.5 text-[14px] leading-[1.55] text-[var(--text-soft)]"
                >
                  <span
                    aria-hidden
                    className="inline-block size-1.5 flex-none translate-y-[-2px] rounded-full bg-[var(--gold-bright)]"
                  />
                  {highlight}
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="mt-auto pt-6">
          <Link to={propertyPath(property)} className="link-arrow">
            View Full Details
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M2 7h10m-4-4 4 4-4 4" stroke="currentColor" strokeWidth="1.2" />
            </svg>
          </Link>
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Compact card row — sliding strip of spec cards                      */
/* ------------------------------------------------------------------ */

function CompactCardRow({
  properties,
  coverOffsets,
}: {
  properties: Property[];
  coverOffsets: Map<string, number>;
}) {
  const track = useScrollTrack([properties.length]);

  return (
    <div className="mt-10 sm:mt-14 short:mt-8">
      <SliderArrows
        className="mb-4"
        label="properties"
        canPrev={track.canPrev}
        canNext={track.canNext}
        onPrev={() => track.scrollByDir("prev")}
        onNext={() => track.scrollByDir("next")}
      />

      <ul
        ref={track.trackRef}
        onScroll={track.onScroll}
        className="scroll-track gap-4 sm:gap-5"
      >
        {properties.map((property) => (
          <li
            key={property.id}
            className="w-[80%] sm:w-[44%] md:w-[31%] lg:w-[23.5%]"
          >
            <CompactPropertyCard
              property={property}
              coverOffset={coverOffsets.get(property.id) ?? 0}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function CompactPropertyCard({
  property,
  coverOffset,
}: {
  property: Property;
  coverOffset: number;
}) {
  const title = formatListingTitle(property);
  const cover = rankPropertyImages(property.images, property.id, { coverOffset })[0];

  return (
    <Link to={propertyPath(property)} className="group flex h-full flex-col">
      <div className="relative aspect-[16/10] overflow-hidden bg-[var(--surface-2)]">
        <SafeImage
          src={cover}
          alt={title}
          loading="lazy"
          sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 80vw"
          widths={[360, 540, 720, 960]}
          className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]"
        />
        <span className="absolute left-3 top-3 inline-flex items-center bg-white px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[var(--navy)]">
          {badgeLabel(property)}
        </span>
      </div>

      <div className="flex flex-1 flex-col border border-t-0 border-[var(--line)] p-4">
        {/* Two lines, always reserved, so every card in the row is the
            same height whether its title wraps or not. */}
        <h3
          className="line-clamp-2 min-h-[2.5em] font-display text-[17px] font-semibold leading-[1.25] tracking-[-0.02em] text-[var(--text)] transition-colors group-hover:text-[var(--gold-bright)] sm:text-[18px]"
          title={title}
        >
          {title}
        </h3>

        <SpecRow property={property} className="mt-3.5" compact />

        <div className="mt-auto flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-t border-[var(--line)] pt-3.5">
          <span className="text-[11px] uppercase tracking-[0.14em] text-[var(--text-muted)]">
            {priceCaption(property)}
          </span>
          <span className="font-display text-[18px] font-semibold tracking-[-0.02em] text-[var(--gold-bright)] tabular-nums sm:text-[19px]">
            {formatPrice(property.price, property.pricePeriod)}
          </span>
        </div>
      </div>
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/* Shared bits                                                         */
/* ------------------------------------------------------------------ */

/** Icon spec row — bed / bath / built-up area, reference-style. */
function SpecRow({
  property,
  className,
  compact = false,
}: {
  property: Property;
  className?: string;
  compact?: boolean;
}) {
  const textClass = compact
    ? "text-[12.5px] text-[var(--text-soft)]"
    : "text-[13.5px] text-[var(--text-soft)]";
  const iconClass = compact ? "size-4" : "size-[18px]";
  return (
    <ul className={cn("flex flex-wrap items-center gap-x-4 gap-y-2 sm:gap-x-5", textClass, className)}>
      <li className="flex items-center gap-1.5">
        <BedDouble className={cn(iconClass, "text-[var(--gold)]")} strokeWidth={1.5} aria-hidden />
        {property.bedrooms === 0 ? "Studio" : `${property.bedrooms} Bed`}
      </li>
      {property.bathrooms > 0 && (
        <li className="flex items-center gap-1.5">
          <Bath className={cn(iconClass, "text-[var(--gold)]")} strokeWidth={1.5} aria-hidden />
          {property.bathrooms} Bath
        </li>
      )}
      {property.area > 0 && (
        <li className="flex items-center gap-1.5">
          <Ruler className={cn(iconClass, "text-[var(--gold)]")} strokeWidth={1.5} aria-hidden />
          {formatArea(property.area)}
        </li>
      )}
    </ul>
  );
}

function badgeLabel(property: Property): string {
  return (
    property.isExclusive
      ? "Exclusive"
      : property.listingType === "off-plan"
        ? "Off-Plan"
        : property.listingType === "rent"
          ? "For Rent"
          : "For Sale"
  ).toUpperCase();
}

/** Price caption in the reference's "Starting Price:" style. */
function priceCaption(property: Property): string {
  if (property.listingType === "off-plan") return "Starting Price";
  if (property.listingType === "rent") return "Rent";
  return "Price";
}

function ShowcaseSkeleton() {
  return (
    <div className="mt-8 sm:mt-10">
      <div className="grid gap-6 sm:gap-8 md:grid-cols-12 md:gap-12">
        <div className="md:col-span-7">
          <div className="aspect-[4/3] animate-pulse bg-[var(--surface-2)] sm:aspect-[16/10]" />
        </div>
        <div className="md:col-span-5 flex flex-col gap-4">
          <div className="h-8 w-3/4 animate-pulse bg-[var(--surface-2)]" />
          <div className="h-4 w-1/3 animate-pulse bg-[var(--surface-2)]" />
          <div className="h-12 w-full animate-pulse bg-[var(--surface-2)]" />
          <div className="h-24 w-full animate-pulse bg-[var(--surface-2)]" />
        </div>
      </div>
      <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex flex-col gap-3">
            <div className="aspect-[16/10] animate-pulse bg-[var(--surface-2)]" />
            <div className="h-4 w-2/3 animate-pulse bg-[var(--surface-2)]" />
            <div className="h-3 w-1/2 animate-pulse bg-[var(--surface-2)]" />
          </div>
        ))}
      </div>
    </div>
  );
}
